import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared hook for hive types — merges admin-configured types + user custom types.
 * Used by AddHiveDialog, EditHiveDialog, HiveCosts, BulkAddHives, SplitWizard, etc.
 */
const useHiveTypes = () => {
  const [adminTypes, setAdminTypes] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      const [adminRes, userRes] = await Promise.all([
        axios.get(`${API}/config/hive_types`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/config/user-hive-types`, { headers }).catch(() => ({ data: [] }))
      ]);
      setAdminTypes(Array.isArray(adminRes.data) ? adminRes.data : []);
      setUserTypes(Array.isArray(userRes.data) ? userRes.data : []);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  // Merged list for dropdowns: admin types + user custom types
  const allTypes = useMemo(() => {
    const merged = adminTypes.map(t => ({
      id: t.id,
      value: t.name?.toLowerCase().replace(/\s+/g, '_'),
      label: t.name,
      source: 'admin',
      frame_types: t.frame_types || [],
      foundation_types: t.foundation_types || [],
      box_types: t.box_types || [],
      features: t.features || [],
    }));
    userTypes.forEach(t => {
      merged.push({
        id: t.id,
        value: `custom_${t.id}`,
        label: t.name,
        source: 'user',
        frame_types: t.frame_types || [],
        foundation_types: t.foundation_types || [],
        box_types: t.box_types || [],
        features: t.features || [],
      });
    });
    return merged;
  }, [adminTypes, userTypes]);

  // Find a type config by value (handles both admin and custom)
  const findType = useCallback((hiveTypeValue) => {
    if (!hiveTypeValue) return null;
    return allTypes.find(t => {
      if (t.value === hiveTypeValue) return true;
      if (t.label?.toLowerCase().replace(/\s+/g, '_') === hiveTypeValue) return true;
      if (t.label?.toLowerCase() === hiveTypeValue) return true;
      return false;
    });
  }, [allTypes]);

  // Get frame types for a hive type
  const getFrameTypes = useCallback((hiveTypeValue) => {
    const cfg = findType(hiveTypeValue);
    return cfg?.frame_types?.length > 0 ? cfg.frame_types : ['Plastic', 'Wooden', 'Specialty', 'Flow Frames'];
  }, [findType]);

  // Get foundation types for a hive type
  const getFoundationTypes = useCallback((hiveTypeValue) => {
    const cfg = findType(hiveTypeValue);
    return cfg?.foundation_types?.length > 0 ? cfg.foundation_types : ['Wax', 'Plastic', 'Waxed Plastic', 'Strips', 'Foundationless'];
  }, [findType]);

  return {
    adminTypes,
    userTypes,
    allTypes,
    loading,
    findType,
    getFrameTypes,
    getFoundationTypes,
    refresh: fetchTypes
  };
};

export default useHiveTypes;
